import React, { useState, useEffect } from 'react';
import './ProgressBar.css';

// Individual progress bar for a single operation
const SingleProgressBar = ({ operation }) => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Smooth progress animation
  useEffect(() => {
    if (operation.progress !== displayProgress) {
      setIsAnimating(true);
      const duration = 300; // Animation duration in ms
      const startProgress = displayProgress;
      const endProgress = operation.progress;
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentProgress = startProgress + (endProgress - startProgress) * easeOutQuart;
        
        setDisplayProgress(currentProgress);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setIsAnimating(false);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [operation.progress, displayProgress]);

  // Get phase color
  const getPhaseColor = (phase) => {
    switch (phase) {
      case 'setup':
      case 'creation':
        return '#007bff';
      case 'validation':
        return '#28a745';
      case 'fix':
        return '#ffc107';
      default:
        return '#6c757d';
    }
  };

  // Get operation type icon
  const getOperationIcon = (operationType) => {
    switch (operationType) {
      case 'create':
        return '➕';
      case 'bulk-create':
        return '📦';
      case 'validate':
      case 'validate-multiple':
      case 'validate-all':
        return '🔍';
      case 'fix':
      case 'fix-multiple':
        return '🔧';
      default:
        return '⚙️';
    }
  };

  // Format duration
  const formatDuration = (startTime) => {
    if (!startTime) return '';
    const elapsed = Date.now() - startTime;
    const seconds = Math.floor(elapsed / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className={`progress-bar-container ${operation.completed ? 'completed' : ''} ${isAnimating ? 'animating' : ''}`}>
      <div className="progress-header">
        <div className="progress-title">
          <span className="operation-icon">{getOperationIcon(operation.operationType)}</span>
          <span className="operation-description">{operation.description}</span>
        </div>
        <div className="progress-stats">
          <span className="progress-percentage">{Math.round(displayProgress)}%</span>
          <span className="progress-duration">{formatDuration(operation.startTime)}</span>
        </div>
      </div>
      
      <div className="progress-bar-wrapper">
        <div 
          className="progress-bar"
          style={{
            width: `${displayProgress}%`,
            backgroundColor: getPhaseColor(operation.phase)
          }}
        />
      </div>
      
      <div className="progress-details">
        <div className="progress-task">
          {operation.taskDescription || 'Initializing...'}
        </div>
        <div className="progress-count">
          {operation.completedTasks} / {operation.totalTasks} tasks
        </div>
      </div>
      
      {operation.completed && (
        <div className={`progress-completion ${operation.success ? 'success' : 'error'}`}>
          {operation.success ? '✅' : '❌'} {operation.message}
        </div>
      )}
    </div>
  );
};

// Main progress bar component that handles multiple operations
const ProgressBar = ({ operations = [], onClearCompleted }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const activeOperations = operations.filter(op => !op.completed);
  const completedOperations = operations.filter(op => op.completed);
  const hasActiveOperations = activeOperations.length > 0;
  const hasCompletedOperations = completedOperations.length > 0;

  // Auto-expand when there are active operations
  useEffect(() => {
    if (hasActiveOperations && !isExpanded) {
      setIsExpanded(true);
    }
  }, [hasActiveOperations, isExpanded]);

  // Auto-collapse when no active operations
  useEffect(() => {
    if (!hasActiveOperations && isExpanded) {
      setTimeout(() => setIsExpanded(false), 2000);
    }
  }, [hasActiveOperations, isExpanded]);

  if (!hasActiveOperations && !hasCompletedOperations) {
    return null;
  }

  return (
    <div className={`progress-bar-panel ${isExpanded ? 'expanded' : ''}`}>
      <div className="progress-header-bar" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="progress-summary">
          <span className="progress-icon">⚙️</span>
          <span className="progress-text">
            {hasActiveOperations 
              ? `${activeOperations.length} operation${activeOperations.length > 1 ? 's' : ''} in progress`
              : `${completedOperations.length} operation${completedOperations.length > 1 ? 's' : ''} completed`
            }
          </span>
        </div>
        <div className="progress-controls">
          {hasCompletedOperations && (
            <button 
              className="clear-completed-btn"
              onClick={(e) => {
                e.stopPropagation();
                onClearCompleted();
              }}
              title="Clear completed operations"
            >
              🗑️
            </button>
          )}
          <button 
            className="expand-toggle"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? '▼' : '▲'}
          </button>
        </div>
      </div>
      
      {isExpanded && (
        <div className="progress-content">
          {/* Active operations */}
          {activeOperations.map(operation => (
            <SingleProgressBar key={operation.id} operation={operation} />
          ))}
          
          {/* Completed operations */}
          {completedOperations.length > 0 && (
            <>
              {showAll ? (
                completedOperations.map(operation => (
                  <SingleProgressBar key={operation.id} operation={operation} />
                ))
              ) : (
                completedOperations.slice(0, 2).map(operation => (
                  <SingleProgressBar key={operation.id} operation={operation} />
                ))
              )}
              
              {completedOperations.length > 2 && (
                <div className="show-more-container">
                  <button 
                    className="show-more-btn"
                    onClick={() => setShowAll(!showAll)}
                  >
                    {showAll 
                      ? `Show less (${completedOperations.length - 2} hidden)`
                      : `Show ${completedOperations.length - 2} more completed`
                    }
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgressBar; 